import { Campaign, Brand, User } from './types';
import { CampaignStatus, UserRole, Department, AdminView } from './enums';

export const INITIAL_BRANDS: Brand[] = [
  { id: '1', name: "Asiana" },
  { id: '2', name: "Bennigan's" },
  { id: '3', name: "Fuddruckers" },
  { id: '4', name: "Coffee Beanery" },
  { id: '5', name: "Caravan Bukhara" },
  { id: '6', name: "Star of India" },
  { id: '7', name: "Star of India Al Wakrah" },
  { id: '8', name: "Ponderosa" },
  { id: '9', name: "Sterling Grill" },
  { id: '10', name: "Sterling Fast Food" },
  { id: '11', name: "Shebestan Palace" },
  { id: '12', name: "TCBY" },
  { id: '13', name: "Twinky" }
];

export const AGGREGATORS = [
  "Talabat",
  "Snoonu",
  "Rafeeq",
  "Deliveroo",
  "Keeta"
];

// Lazy-initialized to avoid TDZ issues with enum imports
let _initialCampaigns: Campaign[] | null = null;
export const getInitialCampaigns = (): Campaign[] => {
  if (!_initialCampaigns) {
    _initialCampaigns = [
      {
        id: '1',
        name: 'July Discount Campaign for Delivery partners',
        discountType: 'Percentage',
        discountValue: 30,
        aggregators: ['Talabat', 'Snoonu', 'Rafeeq', 'Deliveroo', 'Keeta'],
        startDate: '2025-07-01',
        endDate: '2025-07-31',
        status: CampaignStatus.ACTIVE,
        description: 'This campaign applies to major aggregators for the entire month of July.',
        adminWarning: 'Please ensure your item names match the aggregator menu exactly so our backend can map them correctly.',
        fundingType: 'Co-Funded',
        sterlingPercent: 50,
        coFundedPercent: 50
      }
    ];
  }
  return _initialCampaigns;
};

// Backward-compatible export
export const INITIAL_CAMPAIGNS: Campaign[] = new Proxy([] as Campaign[], {
  get(target, prop) {
    const campaigns = getInitialCampaigns();
    if (prop === 'length') return campaigns.length;
    if (typeof prop === 'string' && !isNaN(Number(prop))) return campaigns[Number(prop)];
    if (prop === Symbol.iterator) return campaigns[Symbol.iterator].bind(campaigns);
    return Reflect.get(campaigns, prop);
  },
});

// Helper to grant all permissions - lazy initialized
let _allPermissions: AdminView[] | null = null;
export const getAllPermissions = (): AdminView[] => {
  if (!_allPermissions) {
    _allPermissions = [
      // Page permissions
      AdminView.DASHBOARD,
      AdminView.CAMPAIGNS,
      AdminView.SUBMISSIONS,
      AdminView.PROPOSALS,
      AdminView.TABLE,
      AdminView.REPORTS,
      AdminView.ANALYTICS,
      AdminView.COMMUNITY,
      AdminView.MENU_BUILDER,
      AdminView.SETTINGS,
      AdminView.USERS,
      AdminView.PROFILE,
      AdminView.TRACE,
      AdminView.MANAGER_DASHBOARD,
      AdminView.MANAGERS_DASHBOARD,
      // Action permissions
      AdminView.SUBMIT_SUBMISSIONS,
      AdminView.SUBMIT_PROPOSALS,
      AdminView.SUBMIT_CAMPAIGNS,
    ];
  }
  return _allPermissions;
};

// Backward-compatible export
export const ALL_PERMISSIONS: AdminView[] = new Proxy([] as AdminView[], {
  get(target, prop) {
    const perms = getAllPermissions();
    if (prop === 'length') return perms.length;
    if (typeof prop === 'string' && !isNaN(Number(prop))) return perms[Number(prop)];
    if (prop === Symbol.iterator) return perms[Symbol.iterator].bind(perms);
    return Reflect.get(perms, prop);
  },
});

// Helper for basic users - lazy initialized
let _basicPermissions: AdminView[] | null = null;
export const getBasicPermissions = (): AdminView[] => {
  if (!_basicPermissions) {
    _basicPermissions = [
      AdminView.DASHBOARD,
      AdminView.SUBMISSIONS,
      AdminView.PROPOSALS,
      AdminView.MENU_BUILDER,
      AdminView.PROFILE
    ];
  }
  return _basicPermissions;
};

// Backward-compatible export
export const BASIC_PERMISSIONS: AdminView[] = new Proxy([] as AdminView[], {
  get(target, prop) {
    const perms = getBasicPermissions();
    if (prop === 'length') return perms.length;
    if (typeof prop === 'string' && !isNaN(Number(prop))) return perms[Number(prop)];
    if (prop === Symbol.iterator) return perms[Symbol.iterator].bind(perms);
    return Reflect.get(perms, prop);
  },
});

// Lazy-initialized users
let _initialUsers: User[] | null = null;
export const getInitialUsers = (): User[] => {
  if (!_initialUsers) {
    _initialUsers = [
      {
        id: '1',
        name: 'Super Admin',
        email: 'admin@sterling.qa',
        username: 'admin',
        password: 'password',
        role: UserRole.ADMIN,
        department: Department.ALL,
        active: true,
        permissions: getAllPermissions(),
        profileImage: undefined,
        privilege: 'ADMIN'
      },
      {
        id: '2',
        name: 'SEO Manager',
        email: 'seo@sterling.qa',
        username: 'seo',
        password: 'password',
        role: UserRole.SEO,
        department: Department.ALL,
        active: true,
        permissions: getAllPermissions(),
        profileImage: undefined,
        privilege: 'ADMIN'
      },
      {
        id: '3',
        name: 'F&B Reviewer',
        email: 'fb@sterling.qa',
        username: 'fb_user',
        password: 'password',
        role: UserRole.DEPT_USER,
        department: Department.FB,
        active: true,
        permissions: getBasicPermissions(),
        profileImage: undefined,
        privilege: 'OPERATION'
      },
      {
        id: '4',
        name: 'Finance Reviewer',
        email: 'finance@sterling.qa',
        username: 'fp_user',
        password: 'password',
        role: UserRole.DEPT_USER,
        department: Department.FP,
        active: true,
        permissions: getBasicPermissions(),
        profileImage: undefined,
        privilege: 'OPERATION'
      },
      {
        id: '5',
        name: 'Digital Sales',
        email: 'digital@sterling.qa',
        username: 'ds_user',
        password: 'password',
        role: UserRole.DEPT_USER,
        department: Department.DS,
        active: true,
        permissions: getBasicPermissions(),
        profileImage: undefined,
        privilege: 'OPERATION'
      },
      {
        id: '6',
        name: 'Branch Manager',
        email: 'manager@sterling.qa',
        username: 'manager',
        password: 'password',
        role: UserRole.MANAGER,
        department: Department.OPERATIONS,
        active: true,
        permissions: [AdminView.MANAGER_DASHBOARD, AdminView.SUBMIT_SUBMISSIONS, AdminView.SUBMIT_PROPOSALS, AdminView.COMMUNITY, AdminView.MENU_BUILDER, AdminView.PROFILE], 
        profileImage: undefined,
        privilege: 'OPERATION'
      }
    ];
  }
  return _initialUsers;
};

// Backward-compatible export
export const INITIAL_USERS: User[] = new Proxy([] as User[], {
  get(target, prop) {
    const users = getInitialUsers();
    if (prop === 'length') return users.length;
    if (typeof prop === 'string' && !isNaN(Number(prop))) return users[Number(prop)];
    if (prop === Symbol.iterator) return users[Symbol.iterator].bind(users);
    return Reflect.get(users, prop);
  },
});
